/**
 * MemorialPlaceholder - Elegant placeholder for obituaries without photos
 * Shows initials with optional canton coat of arms or zodiac sign as subtle background
 */

// Swiss canton coat of arms SVG paths (simplified shields)
const CANTON_SHIELDS = {
  zh: { color: '#0066B3', accent: '#FFFFFF', symbol: '/' }, // Zürich - diagonal split
  be: { color: '#D4AF37', accent: '#000000', symbol: '◆' }, // Bern - diagonal bear
  lu: { color: '#1E40AF', accent: '#FFFFFF', symbol: '║' }, // Luzern - blue/white split
  ur: { color: '#FFD700', accent: '#000000', symbol: '◎' }, // Uri - bull head
  sz: { color: '#DC2626', accent: '#FFFFFF', symbol: '✚' }, // Schwyz - cross
  ow: { color: '#DC2626', accent: '#FFFFFF', symbol: '⚷' }, // Obwalden - key
  nw: { color: '#DC2626', accent: '#FFFFFF', symbol: '⚷' }, // Nidwalden - key
  gl: { color: '#000000', accent: '#DC2626', symbol: '✟' }, // Glarus - saint
  zg: { color: '#0066B3', accent: '#FFFFFF', symbol: '═' }, // Zug - stripes
  fr: { color: '#000000', accent: '#FFFFFF', symbol: '║' }, // Fribourg - split
  so: { color: '#DC2626', accent: '#FFFFFF', symbol: '═' }, // Solothurn - stripes
  bs: { color: '#000000', accent: '#FFFFFF', symbol: '⚔' }, // Basel-Stadt - crozier
  bl: { color: '#DC2626', accent: '#FFFFFF', symbol: '⚔' }, // Basel-Land - crozier
  sh: { color: '#D4AF37', accent: '#000000', symbol: '⬢' }, // Schaffhausen - ram
  ar: { color: '#000000', accent: '#FFFFFF', symbol: '🐻' }, // Appenzell AR - bear
  ai: { color: '#000000', accent: '#FFFFFF', symbol: '🐻' }, // Appenzell AI - bear
  sg: { color: '#2E8B57', accent: '#FFFFFF', symbol: '❋' }, // St. Gallen - fasces
  gr: { color: '#475569', accent: '#FFFFFF', symbol: '⬡' }, // Graubünden - ibex
  ag: { color: '#0066B3', accent: '#FFFFFF', symbol: '★' }, // Aargau - stars
  tg: { color: '#2E8B57', accent: '#FFFFFF', symbol: '◆' }, // Thurgau - lions
  ti: { color: '#DC2626', accent: '#0066B3', symbol: '║' }, // Ticino - split
  vd: { color: '#2E8B57', accent: '#FFFFFF', symbol: 'L' }, // Vaud - Liberté
  vs: { color: '#DC2626', accent: '#FFFFFF', symbol: '★' }, // Valais - stars
  ne: { color: '#2E8B57', accent: '#D4AF37', symbol: '✚' }, // Neuchâtel - cross
  ge: { color: '#D4AF37', accent: '#DC2626', symbol: '☀' }, // Genève - eagle/key
  ju: { color: '#DC2626', accent: '#FFFFFF', symbol: '⚔' }, // Jura - crozier
};

// Zodiac sign symbols
const ZODIAC_SYMBOLS = {
  widder: '♈',
  stier: '♉',
  zwillinge: '♊',
  krebs: '♋',
  loewe: '♌',
  jungfrau: '♍',
  waage: '♎',
  skorpion: '♏',
  schuetze: '♐',
  steinbock: '♑',
  wassermann: '♒',
  fische: '♓',
};

export default function MemorialPlaceholder({ 
  vorname, 
  nachname, 
  kantonCode, 
  sternzeichen,
  size = 'md', // 'sm', 'md', 'lg', 'xl'
  className = '',
  showBackground = true,
}) {
  const initials = `${vorname?.[0] || ''}${nachname?.[0] || ''}`.toUpperCase();
  
  // Get canton info
  const canton = kantonCode ? CANTON_SHIELDS[kantonCode.toLowerCase()] : null;
  
  // Get zodiac symbol
  const zodiacKey = sternzeichen?.toLowerCase()?.replace(/ä/g, 'ae')?.replace(/ö/g, 'oe')?.replace(/ü/g, 'ue');
  const zodiacSymbol = zodiacKey ? ZODIAC_SYMBOLS[zodiacKey] : null;
  
  // Size classes
  const sizes = {
    sm: { container: 'w-16 h-16', initials: 'text-xl', background: 'text-4xl' },
    md: { container: 'w-24 h-24 sm:w-32 sm:h-32', initials: 'text-3xl sm:text-4xl', background: 'text-6xl sm:text-7xl' },
    lg: { container: 'w-32 h-32 sm:w-40 sm:h-40', initials: 'text-4xl sm:text-5xl', background: 'text-7xl sm:text-8xl' },
    xl: { container: 'w-40 h-40 sm:w-48 sm:h-48', initials: 'text-5xl sm:text-6xl', background: 'text-8xl sm:text-9xl' },
    full: { container: 'w-full h-full', initials: 'text-5xl sm:text-6xl', background: 'text-8xl sm:text-9xl' },
  };
  
  const sizeConfig = sizes[size] || sizes.md;
  
  // Background gradient based on canton or default
  const bgGradient = canton 
    ? `linear-gradient(135deg, ${canton.color}15 0%, ${canton.accent}10 100%)`
    : 'linear-gradient(135deg, #F8FAFC 0%, #E2E8F0 100%)';
  
  return (
    <div 
      className={`relative flex items-center justify-center overflow-hidden ${sizeConfig.container} ${className}`}
      style={{ background: bgGradient }}
      data-testid="memorial-placeholder"
    >
      {/* Subtle background symbol */}
      {showBackground && (zodiacSymbol || canton?.symbol) && (
        <div 
          className={`absolute inset-0 flex items-center justify-center ${sizeConfig.background} opacity-[0.06] select-none pointer-events-none`}
          style={{ color: canton?.color || '#64748B' }}
        >
          {zodiacSymbol || canton?.symbol}
        </div>
      )}
      
      {/* Decorative border frame */}
      <div className="absolute inset-2 border border-[#CBD5E1]/30 rounded-sm pointer-events-none" />
      
      {/* Corner decorations */}
      <svg className="absolute top-1 left-1 w-3 h-3 text-[#94A3B8]/40" viewBox="0 0 12 12">
        <path d="M0 0 L12 0 L12 2 L2 2 L2 12 L0 12 Z" fill="currentColor"/>
      </svg>
      <svg className="absolute top-1 right-1 w-3 h-3 text-[#94A3B8]/40 transform rotate-90" viewBox="0 0 12 12">
        <path d="M0 0 L12 0 L12 2 L2 2 L2 12 L0 12 Z" fill="currentColor"/>
      </svg>
      <svg className="absolute bottom-1 left-1 w-3 h-3 text-[#94A3B8]/40 transform -rotate-90" viewBox="0 0 12 12">
        <path d="M0 0 L12 0 L12 2 L2 2 L2 12 L0 12 Z" fill="currentColor"/>
      </svg>
      <svg className="absolute bottom-1 right-1 w-3 h-3 text-[#94A3B8]/40 transform rotate-180" viewBox="0 0 12 12">
        <path d="M0 0 L12 0 L12 2 L2 2 L2 12 L0 12 Z" fill="currentColor"/>
      </svg>
      
      {/* Initials */}
      <span 
        className={`relative z-10 font-serif tracking-wider ${sizeConfig.initials}`}
        style={{ 
          color: canton?.color || '#64748B',
          textShadow: '0 1px 2px rgba(255,255,255,0.8)',
          fontWeight: 300,
        }}
      >
        {initials || '?'}
      </span>
      
      {/* Canton indicator (small badge) */}
      {kantonCode && (
        <div 
          className="absolute bottom-1.5 right-1.5 px-1 py-0.5 text-[8px] font-medium rounded-sm bg-white/80 backdrop-blur-sm"
          style={{ color: canton?.color || '#64748B' }}
        >
          {kantonCode.toUpperCase()}
        </div>
      )}
    </div>
  );
}
